import { createColumnHelper } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { WorkspacesCommandList } from '~/features/command-lists/workspaces'
import { useDatastore } from '~/features/datastore'
import { DateCell } from '~/features/table/cells/date'
import { InlineEditCell } from '~/features/table/cells/inline-edit'
import { InlinePopoverCell } from '~/features/table/cells/inline-popover'
import { createPlacehodlerData } from '~/features/table/ui/table'
import { IntentMutators } from '~/mutators/intents'
import { RouterOutput } from '~/services/trpc'

import { FilterCollection } from '@hap/analytics'
import {
  AlertDialog,
  AlertDialogTrigger,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
} from '@hap/components'

export const INTENT_FALLBACK_DATA =
  createPlacehodlerData<RouterOutput['intents']['list'][0]>()

const columnHelper = createColumnHelper<
  RouterOutput['intents']['list'][0] & { editing?: boolean; onSubmit?: () => void }
>()
export const columns = [
  columnHelper.accessor('label', {
    header: 'Name',
    size: 0,
    enableSorting: true,
    cell: ({ getValue, row }) => {
      const [editValue, setEditValue] = useState<string | null>(null)
      const { update } = IntentMutators.useUpdate({
        onSuccess: row.original.onSubmit,
      })
      const { create } = IntentMutators.useCreate({
        onSuccess: row.original.onSubmit,
      })

      useEffect(() => {
        if (row.original.editing && editValue === null) {
          setEditValue(getValue())
        }
      }, [row.original.editing])

      return (
        <InlineEditCell
          value={getValue()}
          editValue={editValue}
          onEditValueChange={setEditValue}
          editTargetId={`${row.original.id}-label`}
          placeholder="Enter intent name"
          onSubmit={(value) => {
            if (row.original.id) {
              update({ intentId: row.original.id, values: { label: value } })
            } else {
              create({ label: value })
            }
          }}
          onCancel={!row.original.id ? row.original.onSubmit : undefined}
        />
      )
    },
  }),
  columnHelper.accessor('workspaceId', {
    header: 'Scope',
    size: 200,
    enableSorting: true,
    cell: ({ getValue, row }) => {
      const workspaceId = getValue()
      const { data: workspaces } = useDatastore(FilterCollection.Workspaces)
      const [open, setOpen] = useState(false)
      const { update } = IntentMutators.useUpdate()

      const workspace = workspaces?.find((w) => w.id === workspaceId)
      const isNew = row.original.id === ''

      if (isNew) return null

      return (
        <InlinePopoverCell
          open={open}
          onOpenChange={setOpen}
          minWidth={120}
          content={
            <WorkspacesCommandList
              selected={workspaceId ? [workspaceId] : []}
              onSelect={(selectedWorkspaceIds) => {
                const selectedWorkspaceId = selectedWorkspaceIds?.[0] ?? null
                const newWorkspaceId =
                  selectedWorkspaceId === 'null' ? null : selectedWorkspaceId
                update({
                  intentId: row.original.id,
                  values: { workspaceId: newWorkspaceId || undefined },
                })
                setOpen(false)
              }}
              allowEmpty
              allowEmptyLabel={
                <>
                  <Icon name="globe" className="size-4" />
                  Organization-wide
                </>
              }
            />
          }
        >
          {workspaceId ? (
            <>
              <Icon name="squares-four" className="size-4 opacity-50" />
              {workspace?.name || 'Unknown'}
            </>
          ) : (
            <>
              <Icon name="globe" className="size-4 opacity-50" />
              Organization-wide
            </>
          )}
        </InlinePopoverCell>
      )
    },
  }),
  columnHelper.accessor('createdAt', {
    header: 'Created',
    size: 120,
    enableSorting: true,
    cell: DateCell(),
  }),
  columnHelper.display({
    id: 'actions',
    size: 60,
    cell: ({ row }) => {
      const [menuOpen, setMenuOpen] = useState(false)
      const { destroy } = IntentMutators.useDestroy()
      const { confirm } = IntentMutators.useConfirm()
      const isNew = row.original.id === ''
      const isSuggested = !row.original.isActive

      return isNew ? null : (
        <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" icon="dots-three-outline" size="icon" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              icon="pencil-simple"
              onClick={() => {
                const selector = `[data-edit-target="${row.original.id}-label"]`
                const elements = document.querySelectorAll<HTMLElement>(selector)
                if (elements.length) elements[0].click()
                else console.warn('No element found with selector: ' + selector)
                setMenuOpen(false)
              }}
            >
              Edit name
            </DropdownMenuItem>

            {isSuggested && (
              <DropdownMenuItem
                icon="check-circle"
                onClick={() => {
                  confirm(row.original.id)
                  setMenuOpen(false)
                }}
              >
                Confirm intent
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <AlertDialog
              title="Delete intent"
              description="Please confirm you want to delete this intent. This action cannot be undone."
              action={{
                value: 'Delete',
                variant: 'destructive',
                onClick: () => {
                  destroy(row.original.id)
                  setMenuOpen(false)
                },
              }}
            >
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={(e) => {
                    e.preventDefault()
                  }}
                  icon="trash"
                >
                  Delete intent...
                </DropdownMenuItem>
              </AlertDialogTrigger>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      )
    },
  }),
]
